import { useEffect, useMemo, useState } from 'react';
import { LogOut, Clock, Ban, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/context/AuthContext';
import { getAdminStats, getActiveStudioId, onActiveStudioChange, setActiveStudioId } from '@/lib/api';
import { ADMIN_DASHBOARD, ADMIN_INTELLIGENCE, ADMIN_LEADS, ADMIN_BOOKINGS, ADMIN_PACKAGES, ADMIN_ANALYTICS, ADMIN_SCOUT, ADMIN_TENANT, ADMIN_PHOTOGRAPHERS, ADMIN_EARNINGS, ADMIN_REVENUE, ADMIN_GEAR_LIBRARY, ADMIN_JOURNAL_TABLE } from '@/constants/testIds';
import { AdminOverviewTab } from '@/components/admin/AdminOverviewTab';
import { AdminGalleriesTab } from '@/components/admin/AdminGalleriesTab';
import { AdminJournalTab } from '@/components/admin/AdminJournalTab';
import { AdminGearLibraryTab } from '@/components/admin/AdminGearLibraryTab';
import { AdminClientsTab } from '@/components/admin/AdminClientsTab';
import { AdminMessagesTab } from '@/components/admin/AdminMessagesTab';
import { AdminNewsletterTab } from '@/components/admin/AdminNewsletterTab';
import { AdminSettingsTab } from '@/components/admin/AdminSettingsTab';
import { AdminIntelligenceTab } from '@/components/admin/AdminIntelligenceTab';
import { AdminLeadsTab } from '@/components/admin/AdminLeadsTab';
import { AdminTeamTab } from '@/components/admin/AdminTeamTab';
import { AdminBookingsTab } from '@/components/admin/AdminBookingsTab';
import { AdminPackagesTab } from '@/components/admin/AdminPackagesTab';
import { AdminAnalyticsTab } from '@/components/admin/AdminAnalyticsTab';
import { AdminScoutTab } from '@/components/admin/AdminScoutTab';
import { AdminMyProfileTab } from '@/components/admin/AdminMyProfileTab';
import { AdminPhotographersTab } from '@/components/admin/AdminPhotographersTab';
import { AdminEarningsTab } from '@/components/admin/AdminEarningsTab';
import { AdminRevenueTab } from '@/components/admin/AdminRevenueTab';
import { TenantSwitcher } from '@/components/admin/TenantSwitcher';

// `owner: true` tabs touch LXRA-global content (journal, team, settings...) and
// are only shown to the LXRA owner. Photographers get their own scoped tabs.
const TABS = [
  { value: 'overview', label: 'Overview', testid: ADMIN_DASHBOARD.tabOverview, Component: AdminOverviewTab },
  { value: 'intelligence', label: 'Intelligence', testid: ADMIN_INTELLIGENCE.tab, Component: AdminIntelligenceTab },
  { value: 'scout', label: 'Scout', testid: ADMIN_SCOUT.tab, Component: AdminScoutTab },
  { value: 'leads', label: 'Leads', testid: ADMIN_LEADS.tab, Component: AdminLeadsTab },
  { value: 'bookings', label: 'Bookings', testid: ADMIN_BOOKINGS.tab, Component: AdminBookingsTab },
  { value: 'packages', label: 'Packages', testid: ADMIN_PACKAGES.tab, Component: AdminPackagesTab },
  { value: 'earnings', label: 'Earnings', testid: ADMIN_EARNINGS.tab, Component: AdminEarningsTab, needsStudioContext: true },
  { value: 'analytics', label: 'Analytics', testid: ADMIN_ANALYTICS.tab, Component: AdminAnalyticsTab },
  { value: 'galleries', label: 'Galleries', testid: ADMIN_DASHBOARD.tabGalleries, Component: AdminGalleriesTab },
  { value: 'journal', label: 'Journal', testid: ADMIN_DASHBOARD.tabJournal, Component: AdminJournalTab, owner: true },
  { value: 'gear-library', label: 'Gear Library', testid: ADMIN_GEAR_LIBRARY.tab, Component: AdminGearLibraryTab, owner: true },
  { value: 'clients', label: 'Clients', testid: ADMIN_DASHBOARD.tabClients, Component: AdminClientsTab },
  { value: 'team', label: 'Team', testid: ADMIN_DASHBOARD.tabTeam, Component: AdminTeamTab, owner: true },
  { value: 'messages', label: 'Messages', testid: ADMIN_DASHBOARD.tabMessages, Component: AdminMessagesTab, owner: true },
  { value: 'newsletter', label: 'Newsletter', testid: ADMIN_DASHBOARD.tabNewsletter, Component: AdminNewsletterTab, owner: true },
  { value: 'settings', label: 'Settings', testid: ADMIN_DASHBOARD.tabSettings, Component: AdminSettingsTab, owner: true },
  { value: 'photographers', label: 'Photographers', testid: ADMIN_TENANT.tabPhotographers, Component: AdminPhotographersTab, owner: true },
  { value: 'revenue', label: 'Revenue', testid: ADMIN_REVENUE.tab, Component: AdminRevenueTab, owner: true },
  { value: 'my-profile', label: 'My Profile', testid: ADMIN_TENANT.tabMyProfile, Component: AdminMyProfileTab },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const isOwner = user?.role === 'admin';
  const studio = user?.studio;
  const [tab, setTab] = useState('overview');
  const [openRevisions, setOpenRevisions] = useState(0);
  const [requestedBookings, setRequestedBookings] = useState(0);
  const [pendingPhotographers, setPendingPhotographers] = useState(0);
  const [pendingAiDrafts, setPendingAiDrafts] = useState(0);
  // LXRA owner "view as" tenant. Changing it remounts every tab so data refetches under the new scope.
  const [viewAs, setViewAs] = useState(isOwner ? getActiveStudioId() : null);
  useEffect(() => onActiveStudioChange(setViewAs), []);
  useEffect(() => { if (!isOwner && getActiveStudioId()) setActiveStudioId(null); }, [isOwner]);

  const tabs = useMemo(
    () => TABS.filter((t) => (isOwner || !t.owner) && (!t.needsStudioContext || !isOwner || viewAs)),
    [isOwner, viewAs],
  );
  useEffect(() => { if (!tabs.some((t) => t.value === tab)) setTab('overview'); }, [tabs, tab]);

  useEffect(() => {
    getAdminStats()
      .then((s) => { setOpenRevisions(s.open_revision_requests || 0); setRequestedBookings(s.requested_bookings || 0); setPendingPhotographers(s.pending_photographers || 0); setPendingAiDrafts(s.pending_ai_drafts || 0); })
      .catch(() => { setOpenRevisions(0); setRequestedBookings(0); setPendingPhotographers(0); setPendingAiDrafts(0); });
  }, [tab, viewAs]);

  return (
    <div className="min-h-[80vh] bg-[#0A0A0A] px-4 sm:px-6 lg:px-10 py-12" data-testid={ADMIN_DASHBOARD.root}>
      <SEO title="Studio Dashboard" description="LXRA Studios admin dashboard." />
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">Studio Dashboard</p>
            <h1 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">
              {studio && !isOwner ? studio.name : `Welcome, ${user?.email}`}
            </h1>
            {isOwner && <div className="mt-3"><TenantSwitcher onChange={setViewAs} /></div>}
          </div>
          <Button
            onClick={logout}
            variant="outline"
            data-testid={ADMIN_DASHBOARD.logoutButton}
            className="border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:bg-[#141416] hover:text-[hsl(var(--lxra-gold))]"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Log Out
          </Button>
        </div>

        {!isOwner && studio?.status === 'pending' && (
          <div data-testid={ADMIN_TENANT.pendingBanner} className="mb-6 flex items-start gap-3 rounded-xl border border-[#C9A45C]/30 bg-[#C9A45C]/10 px-4 py-3">
            <Clock className="h-4 w-4 text-[#D7B46A] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[hsl(var(--lxra-fg))]">
              <span className="font-medium text-[#D7B46A]">Pending LXRA approval.</span> Your public profile at <span className="lxra-mono">/p/{studio.handle}</span> is hidden until LXRA reviews your application (usually 2–3 business days). Set up your profile, packages and galleries in the meantime.
              <button type="button" onClick={() => setTab('my-profile')} className="ml-2 underline underline-offset-2 text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))]">Edit my profile</button>
            </div>
          </div>
        )}
        {!isOwner && studio?.status === 'suspended' && (
          <div data-testid={ADMIN_TENANT.suspendedBanner} className="mb-6 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-[hsl(var(--lxra-fg))]">
            <Ban className="h-4 w-4 text-red-300 mt-0.5 flex-shrink-0" />
            <span><span className="font-medium text-red-200">Profile suspended.</span> Your public page and booking link are offline. Contact LXRA Studios to resolve this.</span>
          </div>
        )}
        {isOwner && viewAs && (
          <div data-testid={ADMIN_TENANT.viewingAsBanner} className="mb-6 flex items-center gap-3 rounded-xl border border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/10 px-4 py-2.5 text-sm text-[hsl(var(--lxra-fg))]">
            <Eye className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
            <span>You are viewing this dashboard <span className="font-medium">as another studio</span>. Everything you see and change here belongs to them.</span>
            <button type="button" onClick={() => setActiveStudioId(null)} data-testid="admin-viewing-as-exit" className="ml-auto text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))] hover:underline">Back to all studios</button>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap w-full h-auto bg-[#141416] border border-[hsl(var(--lxra-border))] gap-1 p-1">
            {tabs.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                data-testid={t.testid}
                className="relative text-xs uppercase tracking-[0.08em] data-[state=active]:bg-[hsl(var(--lxra-gold))] data-[state=active]:text-[#0A0A0A] text-[hsl(var(--lxra-fg))]"
              >
                {t.label}
                {t.value === 'bookings' && requestedBookings > 0 && (
                  <span
                    data-testid={ADMIN_BOOKINGS.tabBadge}
                    className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-bold text-[#0A0A0A]"
                  >
                    {requestedBookings}
                  </span>
                )}
                {t.value === 'galleries' && openRevisions > 0 && (
                  <span
                    data-testid={ADMIN_DASHBOARD.galleriesTabBadge}
                    className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-bold text-[#0A0A0A]"
                  >
                    {openRevisions}
                  </span>
                )}
                {t.value === 'photographers' && pendingPhotographers > 0 && (
                  <span
                    data-testid={ADMIN_PHOTOGRAPHERS.tabBadge}
                    className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-bold text-[#0A0A0A]"
                  >
                    {pendingPhotographers}
                  </span>
                )}
                {t.value === 'journal' && pendingAiDrafts > 0 && (
                  <span
                    data-testid={ADMIN_JOURNAL_TABLE.pendingDraftsBadge}
                    className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-bold text-[#0A0A0A]"
                    title="AI drafts awaiting review"
                  >
                    {pendingAiDrafts}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map(({ value, Component }) => (
            <TabsContent key={value} value={value} className="mt-8">
              {value === 'overview' && <Component key={viewAs || 'all'} onNavigateToGalleries={() => setTab('galleries')} onNavigateToBookings={() => setTab('bookings')} />}
              {value === 'photographers' && <Component key={viewAs || 'all'} onViewAs={() => setTab('overview')} />}
              {value !== 'overview' && value !== 'photographers' && <Component key={viewAs || 'all'} />}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
